import { addButtonToTableRow } from "../../../ui/table/Utils.js";
import { EventManager } from "../../../events/EventManager.js";
import * as formattingUtils from "../formattingUtils.js";
import { getQueryParams, roundNumber } from "../../../utils/utils.js";
import { applyUnitConversion } from "../../../utils/template/convertUnits.js";

// static markdown parser for re-use
let markdownConverter_ = null

/*
 * A collection of functions for the template engine to apply special formatting for the popups
 */

/**
 * Format lat/lng to a readable string with decimal degrees
 * 
 * @param {string[]} args array containing lat and lng
 * @returns {string} formatted lat/lng
 * 
 * @memberof vef.map.popup.templateFunctions
 */
export function formatLatLng(args, data, postProcessingQueue) {
    if (args.length == 2) {
        const id = `formatLatLng_${postProcessingQueue.length}`;

        postProcessingQueue.push((container) => {
            const target = container.querySelector("#" + id);
            if (!target) return;
            if ((target.parentNode.nodeName == "TD") && (target.parentNode.parentNode.nodeName == "TR")) {
                let toggle = false;
                addButtonToTableRow(target.parentNode.parentNode, ["fas", "fa-retweet"], "Change Format", null, (keyCell, valueCell) => {
                    target.innerText = formattingUtils.formatLatLng(args[0], args[1], (!toggle) ? "degreeminutes" : null);
                    valueCell.title = target.innerText;
                    toggle = !toggle;
                });
            }
        });

        return `<span id="${id}">${formattingUtils.formatLatLng(args[0], args[1])}</span>`;
    }
    return undefined;
}

/**
 * Format lat/lng from the geometry object of a Feature
 * to a readable string with decimal degrees
 * 
 * @param {string[]} args array containing lat and lng
 * @param {object} data full response
 * @returns {string} formatted lat/lng
 * 
 * @memberof vef.map.popup.templateFunctions
 */
export function formatGeometry(myArgs, data, postProcessingQueue) {
    const geometry = myArgs[0];
    // reproject to EPSG:4326
    if (geometry &&
        (geometry.type.toLowerCase() == "point") &&
        data.response &&
        data.response.crs &&
        data.response.crs.properties &&
        (typeof data.response.crs.properties.name == "string")
    ) {
        const name = data.response.crs.properties.name;
        let crs = null;

        if (name.includes("4326")) {
            crs = "EPSG:4326"
        } else if (name.includes("3995")) {
            crs = "EPSG:3995"
        } else if (name.includes("3031")) {
            crs = "EPSG:3031"
        } else if (name.includes("3857")) {
            crs = "EPSG:3857"
        }

        if (crs) {
            const point = proj4(crs, "EPSG:4326", { x: geometry.coordinates[0], y: geometry.coordinates[1] });
            return formatLatLng([point.y, point.x], data, postProcessingQueue);
        }
    }
    return undefined;
}

/**
 * Add a button to request filtering an attribute
 * 
 * @param {string[]} myArgs [column, value]
 * @param {object} data full response
 * @returns {string} span placeholder
 * 
 * @memberof vef.map.popup.templateFunctions
 */
export function addFilterButton(myArgs, data, postProcessingQueue) {
    if (myArgs.length == 2) {
        const id = `formatLatLng_${postProcessingQueue.length}`;

        postProcessingQueue.push((container) => {
            const target = container.querySelector("#" + id);
            if (!target) return;
            if ((target.parentNode.nodeName == "TD") && (target.parentNode.parentNode.nodeName == "TR")) {
                addButtonToTableRow(target.parentNode.parentNode, ["fas", "fa-filter"], "Add Filter", "", () => {
                    EventManager.fire("add_attribute_filter", {
                        column: myArgs[0],
                        value: myArgs[1]
                    });
                    window.manager1 = EventManager;
                });
            }
        });

        return `<span id="${id}"></span>`;
    }
    return undefined;
}

/**
 * Make ISO Timestring more readable
 * 
 * @param {string[]} args array containing date at position 0
 * @returns {string} formatted date
 * @memberof vef.map.popup.templateFunctions
 */
export function formatDate(myArgs, data, postProcessingQueue) {
    if (myArgs[0]) {
        const id = `formatDate_${postProcessingQueue.length}`;

        postProcessingQueue.push((container) => {
            const target = container.querySelector("#" + id);
            if (!target) return;
            if ((target.parentNode.nodeName == "TD") && (target.parentNode.parentNode.nodeName == "TR")) {
                let mode = 0;
                addButtonToTableRow(target.parentNode.parentNode, ["fas", "fa-retweet"], "Change Format", null, (keyCell, valueCell) => {
                    switch (mode) {
                        case 0:
                            target.innerText = new Date(myArgs[0]).toLocaleString();
                            break;
                        case 1:
                            target.innerText = myArgs[0];
                            break;
                        case 2:
                            target.innerText = formattingUtils.formatDate(myArgs[0]);
                            break;
                    }
                    valueCell.title = target.innerText;
                    mode = (mode + 1) % 3;
                });
            }
        });
        return `<span id="${id}">${formattingUtils.formatDate(myArgs[0])}</span>`;
    }
    return undefined;
}

/**
 * Only returns the given string if the first value is not null or undefined
 * 
 * @param {string[]} args [valueToCheck, then, else]
 * @returns {any} given value
 * @memberof vef.map.popup.templateFunctions
 */
export function isDefined(args, data) {
    if ((args[0] !== null) && (args[0] !== undefined)) {
        return args[1];
    } else {
        return args[2] || undefined;
    }
}

/**
 * Concats values if every value is defined (not null or undefined)
 * 
 * @param {string[]} args [concat1, concat2, ...]
 * @returns {string} string
 * @memberof vef.map.popup.templateFunctions
 */
export function concatIfDefined(args, data) {
    let out = "";
    for (let i = 0; i < args.length; ++i) {
        if ((args[i] !== null) && (args[i] !== undefined)) {
            out += args[i];
        } else {
            return undefined;
        }
    }
    return out;
}

/**
 * Encode String to URL Compatible characters
 * 
 * @param {string[]} args [url compponent]
 * @returns {string} string
 * @memberof vef.map.popup.templateFunctions
 */
export function encodeURLComponent(args, data) {
    if (args[0]) {
        return encodeURIComponent(args[0]);
    }
    return undefined;
}

/**
 * Returns the GRAY_INDEX for bathymetry raster data
 * 
 * @param {string[]} args [url component]
 * @returns {string} string
 * @memberof vef.map.popup.templateFunctions
 */
export function getBathymetryGrayIndex(args, data) {
    if (data.properties && ("GRAY_INDEX" in data.properties)) {
        throw ("discard_template");
    }
    if ((args.length > 0) && (args[0] == hide)) return undefined;
    for (let i = 0; i < data.response.features.length; i++) {
        const properties = data.response.features[i].properties;
        if (('GRAY_INDEX' in properties) && (properties.GRAY_INDEX < 0)) return roundNumber(properties.GRAY_INDEX, 2) + " m";
    }
    return undefined;
}

/**
 * Round a number to the given decimals
 * 
 * @param {string[]} args [number, decimals]
 * @returns {string} string
 * @memberof vef.map.popup.templateFunctions
 */
export function round(args, data) {
    const num = (typeof args[0] == "string") ? Number.parseFloat(args[0]) : args[0];
    if (Number.isFinite(num)) {
        let decimals = Number.parseInt(args[1] || 0);
        if (!Number.isFinite(decimals)) decimals = 0;
        return roundNumber(num, decimals);
    }
    return undefined;
}

/**
 * Split a string at a substring and return the given index
 * 
 * @param {string[]} args [string, substring, index]
 * @returns {string} string
 * @memberof vef.map.popup.templateFunctions
 */
export function splitString(args, data) {
    const str = args[0];
    const subStr = args[1];
    if (!str || !subStr) return undefined;

    const index = Number.parseInt(args[2]) || 0;
    const parts = str.split(subStr);

    return (index < parts.length) ? parts[index] : parts[0];
}

/**
 * Split a string at an index and get the substring
 * 
 * @param {string[]} args [string, startIndex, endIndex]
 * @returns {string} string
 * @memberof vef.map.popup.templateFunctions
 */
export function substring(args, data) {
    const str = args[0];

    if (!str) return undefined;

    const start = Number.parseInt(args[1]) || 0;
    const end = Number.parseInt(args[2]) || 0;

    return str.substring(start, end);;
}

/**
 * Defines the title in data-title for the whole popup feature.
 * The PopupRenderer will extract the title from that property to display
 * it in the header. This is directly set in the container.
 * 
 * @param {string[]} args [string1, string2, ...]
 * @returns {string} empty string
 * @memberof vef.map.popup.templateFunctions
 */
export function setTitle(args, data, postProcessingQueue) {
    if (args.length > 0) {
        postProcessingQueue.push(container => {
            container.dataset.title = args.join("");
        });
    }
    return undefined;
}

/**
 * Parses a json string and outputs the value of the given property
 * 
 * @param {string[]} args [jsonstring, key1, key2, ...]
 * @returns {string} resolved value
 * @memberof vef.map.popup.templateFunctions
 */
export function parseJSON(args, data, postProcessingQueue) {
    if (args.length > 1) {
        try {
            let obj = JSON.parse(args[0]);
            for (let i = 1; i < args.length; ++i) {
                if (args[i] in obj) {
                    obj = obj[args[i]];
                } else {
                    return undefined;
                }
            }
            return obj;
        } catch (e) {
            return undefined;
        }
    }
    return undefined;
}

/**
 * Prevents the copy button to be displayed in a table row
 * 
 * @param {string[]} args none
 * @returns {string} temporary span
 * @memberof vef.map.popup.templateFunctions
 */
export function noCopy(args, data, postProcessingQueue) {
    const id = `no_copy_${postProcessingQueue.length}`;

    postProcessingQueue.push(container => {
        const target = container.querySelector("#" + id);
        if (!target) return;

        if ((target.parentNode.tagName == "TD") && (target.parentNode.parentNode.tagName == "TR")) {
            target.parentNode.parentNode.classList.add("no-copy");
        }

        target.remove();
    });

    return `<span id="${id}" style="display:none;"></span>`;
}

/**
 * Prevents content from being rendered by inserting a comment if the site matches
 * 
 * @param {string[]} args [site]
 * @returns {string} resolved value
 * @memberof vef.map.popup.templateFunctions
 */
export function hideOnSiteStart(args, data, postProcessingQueue) {
    const site = args[0];
    const params = getQueryParams();
    if (Array.isArray(params.site)) params.site = params.site[0];

    return (params.site == site) ? "<!--" : "";
}

/**
 * Prevents content from being rendered by inserting a comment if the site matches
 * 
 * @param {string[]} args [site]
 * @returns {string} resolved value
 * @memberof vef.map.popup.templateFunctions
 */
export function hideOnSiteEnd(args, data, postProcessingQueue) {
    const site = args[0];
    const params = getQueryParams();
    if (Array.isArray(params.site)) params.site = params.site[0];

    return (params.site == site) ? "-->" : "";
}


/**
 * Display a layers legend graphic
 * 
 * @param {string[]} args
 * @returns {string} Legend Graphic
 * @memberof vef.map.popup.templateFunctions
 */
export function displayLayerLegend(args, data, postProcessingQueue) {
    const id = `legend_graphic_${postProcessingQueue.length}`;

    postProcessingQueue.push(container => {
        if (!data.layer) return
        const legend = data.layer.getLegendGraphic();
        if (!legend) return;
        const target = container.querySelector("#" + id);
        target.appendChild(legend);
    });

    return `<div id="${id}"></div>`;
}


/**
 * Join Array values as a string
 * 
 * @param {string[]} args [array, separator]
 * @returns {string} 
 * @memberof vef.map.popup.templateFunctions
 */
export function joinArray(args, data, postProcessingQueue) {
    if (Array.isArray(args[0])) {
        return args[0].join(args?.[1] || ", ");
    }
    return args?.[0] || undefined;
}

/**
 * Join Array values as a string
 * 
 * @param {string[]} args [markdown, maxHeight]
 * @returns {string} 
 * @memberof vef.map.popup.templateFunctions
 */
export function parseMarkdown(args, data, postProcessingQueue) {
    if (!args?.[0]) return undefined;

    const id = `markdown_${postProcessingQueue.length}`;

    if (args?.[1]) postProcessingQueue.push(container => {
        const target = container.querySelector("#" + id);
        const maxHeight = Number.parseFloat(args[1]);
        if (Number.isFinite(maxHeight)) {
            setTimeout(() => {
                if (target.clientHeight > maxHeight) {
                    target.style.position = "relative";
                    target.style.overflow = "hidden";
                    target.style.maxHeight = maxHeight + "px";

                    const readMore = document.createElement("a");
                    readMore.classList.add("markdown-readmore");
                    readMore.href = "#";
                    readMore.innerHTML = "<i class='fas fa-eye'></i> read more";
                    readMore.onclick = e => {
                        e.preventDefault();
                        readMore.remove();
                        target.style.maxHeight = "unset";
                    };
                    target.appendChild(readMore);
                }
            }, 0)
        }
    });

    if (!markdownConverter_) markdownConverter_ = new showdown.Converter({
        tables: true,
        noHeaderId: true,
        literalMidWordUnderscores: true
    });

    return `<div id="${id}">${markdownConverter_.makeHtml(args[0])}</div>`;
}

/**
 * Simple unit formatter. Find the best unit prefix.
 * 
 * @param {string[]} args [value, unit, targetUnit, precision]
 * @param {object} data response
 * @returns {string} div placeholder
 * @memberof vef.map.popup.templateFunctions
 */
export function formatUnit(args, data, postProcessingQueue) {

    const id = `unitConverter_${postProcessingQueue.length}`;

    postProcessingQueue.push(container => {
        const target = container.querySelector("#" + id);
        if (!target) return;

        let [value, unit, targetUnit, precision] = args;
        if (!value) return
        if (!targetUnit) targetUnit = unit
        if (!precision) precision = 3

        const HTMLElement = document.createElement('span');
        HTMLElement.innerText = applyUnitConversion(value, unit, targetUnit, precision)

        target.appendChild(HTMLElement);
    });
    return `<div id="${id}"></div>`;
}